import { useEffect, useMemo, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import { deleteAdmin, listAdmins, resetAdminPassword, signup, updateApproval } from '../../services/adminService';

const statusFilters = [
  { value: 'all', label: 'All admins' },
  // The backend stores unapproved accounts as 'no' by default. Treat 'no'
  // as the pending state in the UI so new signups surface under the
  // default filter. This avoids a mismatch between the DB enum and the
  // filter UI which used 'pending'.
  { value: 'no', label: 'Pending approval' },
  { value: 'yes', label: 'Approved' },
];

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  company: '',
  email: '',
  phoneNumber: '',
  password: '',
  confirmPassword: '',
};

const EMPTY_RESET_FORM = {
  password: '',
  confirmPassword: '',
};

const AdminApprovals = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  // Default to showing pending/unapproved admins (stored as 'no' in DB)
  const [filter, setFilter] = useState('no');
  const [updating, setUpdating] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState('');
  const [deletingId, setDeletingId] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetForm, setResetForm] = useState(EMPTY_RESET_FORM);
  const [resetError, setResetError] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const fetchAdmins = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await listAdmins();
        const payload =
          res.data?.admins ||
          res.data?.results ||
          res.data?.data ||
          res.data ||
          [];
        setAdmins(Array.isArray(payload) ? payload : []);
      } catch (err) {
        const msg = err.response?.data?.message || 'Unable to fetch admin accounts';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchAdmins();
  }, []);

  const filteredAdmins = useMemo(() => {
    const query = search.trim().toLowerCase();
    return admins.filter((admin) => {
      // Normalize server-side value: backend uses 'yes' or 'no'. Map falsy to 'no'
      const approved = (admin.approved || admin.status || 'no').toLowerCase();
      const matchesFilter = filter === 'all' || approved === filter;
      if (!matchesFilter) return false;
      if (!query) return true;
      const target = [
        admin.firstName,
        admin.lastName,
        admin.email,
        admin.company,
        admin.phone,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return target.includes(query);
    });
  }, [admins, search, filter]);

  const handleApprovalChange = async (id, nextState) => {
    setUpdating(id + nextState);
    setError('');
    try {
      await updateApproval(id, nextState);
      setAdmins((prev) =>
        prev.map((admin) =>
          admin._id === id
            ? {
                ...admin,
                approved: nextState,
              }
            : admin,
        ),
      );
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update approval status';
      setError(msg);
    } finally {
      setUpdating('');
    }
  };

  const handleFormChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const openResetPassword = (admin) => {
    setResetTarget(admin);
    setResetForm(EMPTY_RESET_FORM);
    setResetError('');
    setMessage('');
  };

  const closeResetPassword = () => {
    setResetTarget(null);
    setResetForm(EMPTY_RESET_FORM);
    setResetError('');
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    if (!resetTarget?._id) return;

    setResetError('');
    setMessage('');
    if (resetForm.password.length < 8) {
      setResetError('Password must be at least 8 characters.');
      return;
    }
    if (resetForm.password !== resetForm.confirmPassword) {
      setResetError("Passwords don't match.");
      return;
    }

    setResetting(true);
    try {
      await resetAdminPassword(resetTarget._id, resetForm);
      setMessage(`Password reset for ${resetTarget.email}.`);
      closeResetPassword();
    } catch (err) {
      setResetError(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setResetting(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    setError('');
    setMessage('');
    try {
      await deleteAdmin(id);
      setAdmins((prev) => prev.filter((admin) => admin._id !== id));
      setConfirmDeleteId('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete admin.');
    } finally {
      setDeletingId('');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setMessage('');
    setSaving(true);
    try {
      await signup({ ...form, approved: 'yes' });
      const res = await listAdmins();
      const payload = res.data?.admins || res.data?.results || res.data?.data || res.data || [];
      setAdmins(Array.isArray(payload) ? payload : []);
      setForm(EMPTY_FORM);
      setShowForm(false);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create admin.');
    } finally {
      setSaving(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return <div className="skeleton" style={{ height: '240px' }} />;
    }
    if (error) {
      return <div className="feedback error">{error}</div>;
    }
    if (!filteredAdmins.length) {
      return <div className="empty-state">No admins match your current filters.</div>;
    }

    return (
      <div className="table-responsive-stack">
        <table className="data-table">
        <thead>
          <tr>
            <th>Admin</th>
            <th>Contact</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAdmins.map((admin) => {
            const approval = (admin.approved || admin.status || 'no').toLowerCase();
            const badgeClass =
              approval === 'yes'
                ? 'badge-success'
                : approval === 'no'
                ? 'badge-info'
                : 'badge-warning';
            return (
              <tr key={admin._id}>
                <td data-label="Admin">
                  <div className="table-stack">
                    <span className="primary">
                      {admin.firstName} {admin.lastName}
                    </span>
                    <span className="secondary">{admin.company || 'No company on file'}</span>
                  </div>
                </td>
                <td data-label="Contact">
                  <div className="table-stack">
                    <span className="primary">{admin.email || '—'}</span>
                    <span className="secondary">{admin.phone || 'No phone provided'}</span>
                  </div>
                </td>
                <td data-label="Status">
                  <span className={`badge ${badgeClass}`}>
                    {approval === 'yes' ? 'Approved' : approval === 'no' ? 'Pending' : 'Unknown'}
                  </span>
                </td>
                <td data-label="Actions">
                  {confirmDeleteId === admin._id ? (
                    <div className="pill-group">
                      <span className="secondary" style={{ fontSize: '0.8rem' }}>Delete?</span>
                      <button
                        type="button"
                        className="pill-button pill-button-danger"
                        disabled={deletingId === admin._id}
                        onClick={() => handleDelete(admin._id)}
                      >
                        {deletingId === admin._id ? 'Deleting...' : 'Yes'}
                      </button>
                      <button
                        type="button"
                        className="pill-button"
                        onClick={() => setConfirmDeleteId('')}
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <div className="pill-group">
                      <button
                        type="button"
                        className="pill-button"
                        disabled={updating === admin._id + 'yes' || approval === 'yes'}
                        onClick={() => handleApprovalChange(admin._id, 'yes')}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="pill-button"
                        disabled={updating === admin._id + 'no' || approval === 'no'}
                        onClick={() => handleApprovalChange(admin._id, 'no')}
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        className="pill-button pill-button-danger"
                        onClick={() => setConfirmDeleteId(admin._id)}
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        className="pill-button"
                        onClick={() => openResetPassword(admin)}
                      >
                        Reset password
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>
    );
  };

  return (
    <AppLayout
      title="Admin access approvals"
      subtitle="Grant or deny access to dispatch tools for new administrators."
      actions={
        <button
          type="button"
          className="pill-button"
          onClick={() => {
            setShowForm((v) => !v);
            setFormError('');
            setForm(EMPTY_FORM);
          }}
        >
          {showForm ? 'Cancel' : 'Add Admin'}
        </button>
      }
    >
      {showForm && (
        <div className="surface" style={{ marginBottom: '1rem' }}>
          <form onSubmit={handleCreate}>
            <div className="form-grid two-column">
              <div>
                <label htmlFor="aa-firstName">First name</label>
                <input
                  id="aa-firstName"
                  name="firstName"
                  type="text"
                  placeholder="Jane"
                  value={form.firstName}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="aa-lastName">Last name</label>
                <input
                  id="aa-lastName"
                  name="lastName"
                  type="text"
                  placeholder="Smith"
                  value={form.lastName}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="aa-company">Company</label>
                <input
                  id="aa-company"
                  name="company"
                  type="text"
                  placeholder="Acme Taxi Co."
                  value={form.company}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="aa-email">Email</label>
                <input
                  id="aa-email"
                  name="email"
                  type="email"
                  placeholder="jane@example.com"
                  value={form.email}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="aa-password">Password</label>
                <input
                  id="aa-password"
                  name="password"
                  type="password"
                  placeholder="Min. 8 characters"
                  value={form.password}
                  onChange={handleFormChange}
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label htmlFor="aa-confirmPassword">Confirm password</label>
                <input
                  id="aa-confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Repeat password"
                  value={form.confirmPassword}
                  onChange={handleFormChange}
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label htmlFor="aa-phoneNumber">Phone number <span style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</span></label>
                <input
                  id="aa-phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  placeholder="+1 555 000 0000"
                  value={form.phoneNumber}
                  onChange={handleFormChange}
                />
              </div>
            </div>
            {formError && (
              <div className="feedback error" style={{ marginTop: '0.75rem' }}>{formError}</div>
            )}
            <div className="pill-group" style={{ marginTop: '1rem' }}>
              <button type="submit" className="pill-button" disabled={saving}>
                {saving ? 'Saving…' : 'Create Admin'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="surface">
        {message && <div className="feedback success" style={{ marginBottom: '0.75rem' }}>{message}</div>}
        <div className="toolbar">
          <div className="search-input">
            <span className="icon">🔍</span>
            <input
              type="search"
              placeholder="Search by name, company or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="filter-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
            {statusFilters.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="summary">{filteredAdmins.length} of {admins.length} admins</div>
        </div>
        {renderBody()}
      </div>
      {resetTarget && (
        <div className="modal-overlay" onClick={closeResetPassword}>
          <div
            className="modal-card"
            style={{ maxWidth: 520 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="reset-admin-password-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="reset-admin-password-title">Reset admin password</h3>
              <button type="button" className="pill-button" onClick={closeResetPassword}>
                Close
              </button>
            </div>
            <form className="modal-body" onSubmit={handleResetPassword}>
              <p className="text-muted" style={{ marginTop: 0 }}>
                Set a new password for {resetTarget.firstName} {resetTarget.lastName} ({resetTarget.email}).
                Their current session will be signed out.
              </p>
              <div className="form-grid">
                <div>
                  <label htmlFor="reset-admin-password">New password</label>
                  <input
                    id="reset-admin-password"
                    type="password"
                    value={resetForm.password}
                    onChange={(event) => setResetForm((prev) => ({ ...prev, password: event.target.value }))}
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="reset-admin-confirm-password">Confirm password</label>
                  <input
                    id="reset-admin-confirm-password"
                    type="password"
                    value={resetForm.confirmPassword}
                    onChange={(event) => setResetForm((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </div>
              </div>
              {resetError && <div className="feedback error" style={{ marginTop: '0.75rem' }}>{resetError}</div>}
              <div className="pill-group" style={{ marginTop: '1rem' }}>
                <button type="submit" className="pill-button" disabled={resetting}>
                  {resetting ? 'Resetting...' : 'Reset password'}
                </button>
                <button type="button" className="pill-button" onClick={closeResetPassword} disabled={resetting}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default AdminApprovals;
